import { useCallback, useMemo } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'

type ReplaceOptions = {
  resetQueries?: string[]
}

export const useQueryHelper = () => {
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const navigate = useNavigate()

  const getPath = (key: string, val: string, options?: ReplaceOptions) => {
    const query = []
    let isFresh = true
    for (const [k, v] of searchParams.entries()) {
      if (k === key) {
        if (val) {
          query.push(`${k}=${val}`)
        }
        isFresh = false
      } else {
        if (!options?.resetQueries?.includes(k)) {
          query.push(`${k}=${v}`)
        }
      }
    }

    if (isFresh) {
      query.push(`${key}=${val}`)
    }

    return `${location.pathname}?${query.join('&')}`
  }

  const query: Record<string, string> = useMemo(() => {
    const query: Record<string, string> = {}
    for (const [k, v] of searchParams.entries()) {
      query[k] = v
    }
    return query
  }, [searchParams])

  const replaceQuery = useCallback(
    (record: Record<string, string | number | boolean | object>) => {
      const _query = { ...query }

      for (const [k, v] of Object.entries(record)) {
        if (typeof v !== 'undefined') {
          if (typeof v === 'object') {
            _query[k] = JSON.stringify(v)
          } else {
            _query[k] = String(v)
          }
        } else {
          if (k in _query) {
            delete _query[k]
          }
        }
      }

      navigate(
        `${location.pathname}?${Object.entries(_query)
          .map(([k, v]) => `${k}=${v}`)
          .join('&')}`,
        {
          preventScrollReset: true,
          replace: true,
        },
      )
    },
    [query, location.pathname, navigate],
  )

  return {
    replaceQuery,
    getPath,
    query,
  }
}
